// =========================================================================
// Signed-in user's core.users profile row.
// =========================================================================

import { supabase } from '../client';

export interface MyUserProfile {
  id: string;
  email: string | null;
  display_name: string | null;
  preferred_locale: string | null;
  account_id: string;
  created_at: string;
  idle_lock_seconds: number | null;
  lock_pin_set_at: string | null;
}

export async function getMyProfile(): Promise<MyUserProfile | null> {
  const { data: sessionData } = await supabase().auth.getUser();
  const uid = sessionData?.user?.id;
  if (!uid) return null;

  const { data, error } = await supabase()
    .schema('core')
    .from('users')
    .select(
      'id, email, display_name, preferred_locale, account_id, created_at, idle_lock_seconds, lock_pin_set_at',
    )
    .eq('id', uid)
    .maybeSingle();
  if (error) throw error;
  return (data as MyUserProfile | null) ?? null;
}

export async function updateMyDisplayName(displayName: string | null): Promise<void> {
  const { data: sessionData } = await supabase().auth.getUser();
  const uid = sessionData?.user?.id;
  if (!uid) throw new Error('Not signed in');

  const trimmed = displayName?.trim() ?? '';
  const { error } = await supabase()
    .schema('core')
    .from('users')
    .update({ display_name: trimmed.length > 0 ? trimmed : null })
    .eq('id', uid);
  if (error) throw error;
}

// =========================================================================
// Universal screen-lock: per-user PIN + idle timeout (core.users).
// PIN is hashed server-side (pgcrypto); set/verified only via these RPCs.
//
// The `as unknown as { rpc }` casts are intentional and mirror
// `services/userSmsAuth.ts`: these RPCs ship in migration
// 20260703040000_core_user_screen_lock_pins.sql and have not been picked
// up by the generated `Database` types yet. The shapes below match each
// RPC's `returns` block and are defensively narrowed.
// =========================================================================

type LooseRpc = {
  rpc: (
    name: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: unknown }>;
};

function coreRpc(): LooseRpc {
  return supabase().schema('core') as unknown as LooseRpc;
}

export interface MyLockSettings {
  has_pin: boolean;
  idle_lock_seconds: number;
  locked_until: string | null;
}

export async function getMyLockSettings(): Promise<MyLockSettings | null> {
  const { data, error } = await coreRpc().rpc('rpc_get_my_lock_settings');
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as MyLockSettings | undefined) ?? null;
}

export async function setMyLockPin(pin: string): Promise<void> {
  const { error } = await coreRpc().rpc('rpc_set_my_lock_pin', { p_pin: pin });
  if (error) throw error;
}

export async function changeMyLockPin(
  currentPin: string,
  newPin: string,
): Promise<void> {
  const { error } = await coreRpc().rpc('rpc_change_my_lock_pin', {
    p_current_pin: currentPin,
    p_new_pin: newPin,
  });
  if (error) throw error;
}

export async function setMyIdleLockSeconds(seconds: number): Promise<void> {
  const { error } = await coreRpc().rpc('rpc_set_my_idle_lock_seconds', {
    p_seconds: seconds,
  });
  if (error) throw error;
}

/** Verify the signed-in user's own PIN. Returns true/false; throws on lockout. */
export async function verifyMyLockPin(pin: string): Promise<boolean> {
  const { data, error } = await coreRpc().rpc('rpc_verify_my_lock_pin', {
    p_pin: pin,
  });
  if (error) throw error;
  return data === true;
}

export interface StationOperator {
  user_id: string;
  display_name: string;
}

/** Org teammates who have a screen-lock PIN set (for the fulfillment swap). */
export async function listStationOperators(
  orgId: string,
): Promise<StationOperator[]> {
  const { data, error } = await coreRpc().rpc('rpc_station_list_operators', {
    p_org_id: orgId,
  });
  if (error) throw error;
  return (data as StationOperator[] | null) ?? [];
}

/**
 * Verify a teammate's PIN on a shared station and return their identity.
 * Throws on incorrect PIN / lockout.
 */
export async function verifyStationOperatorPin(
  orgId: string,
  userId: string,
  pin: string,
): Promise<StationOperator> {
  const { data, error } = await coreRpc().rpc(
    'rpc_station_verify_operator_pin',
    { p_org_id: orgId, p_user_id: userId, p_pin: pin },
  );
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Verification failed');
  return row as StationOperator;
}
